import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";
import { useEffect, useState } from "react";
import "./ProtectedRoute.scss";
import { UserRole } from "../../types";

interface RoleProtectedRouteProps {
  children: JSX.Element;
  allowedRoles: UserRole[];
  blockTrial?: boolean;
}

const RoleProtectedRoute = ({ children, allowedRoles, blockTrial }: RoleProtectedRouteProps) => {
  const { currentUser, loading, userRole, isTrialUser } = useAuth();
  const { t } = useTranslation();
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    // Only show loading indicator for a very short time on initial load
    if (loading) {
      setShowLoading(true);
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 100); // Very short delay to prevent flicker
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // If we're loading and it's the first load, show the loading indicator
  if (loading && showLoading) {
    return (
      <div className="minimal-loading">
        <LoadingIndicator message="" />
      </div>
    );
  }

  // If we're not loading and there's no user, redirect to login
  if (!loading && !currentUser) {
    return <Navigate to="/login" />;
  }


  // Wait for userRole to be available before checking allowedRoles
  if (!loading && currentUser && userRole === null) {
    return (
      <div className="minimal-loading">
        <LoadingIndicator message={t('common.loadingUserRole')} />
      </div>
    );
  }

  // If we have a user but their role is not allowed, redirect to home
  if (!loading && currentUser && userRole && !allowedRoles.includes(userRole)) {
    return <Navigate to="/" />;
  }

  if (blockTrial && isTrialUser) {
    return <Navigate to="/" replace />;
  }

  // If we have a user with the correct role or we're still loading (but not showing the loading indicator),
  // render the children
  return children;
};

export default RoleProtectedRoute; 